import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, UserIcon, BoxIcon } from "lucide-react";
interface ContactStep {
  icon: BoxIcon;
  title: string;
  description: string;
}
const contactSteps: ContactStep[] = [{
  icon: ClockIcon,
  title: 'Revisamos tu mensaje',
  description: 'Leemos tu contexto y detectamos los puntos que conviene abordar primero.'
}, {
  icon: MessageSquareIcon,
  title: 'Definimos la conversación',
  description: 'Te respondemos para entender prioridades, materiales y alcance.'
}, {
  icon: CalendarIcon,
  title: 'Aterrizamos el siguiente paso',
  description: 'Si hay encaje, acordamos la forma más clara de avanzar.'
}];
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormStatus('submitting');
    window.setTimeout(() => {
      setFormStatus('success');
      window.setTimeout(() => setFormStatus('idle'), 4000);
    }, 900);
  };
  const updateField = (field: keyof typeof formData, value: string) => setFormData(current => ({
    ...current,
    [field]: value
  }));
  const inputClass = 'w-full border border-white/15 bg-black px-4 py-3.5 text-sm text-white transition-colors placeholder:text-neutral-600 focus:border-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
  return <div className="min-h-screen bg-[#0a0a0a] pt-24" data-id="element-287" data-mp="3qh3gsqu99g71">
      <section className="border-b border-white/10 py-16 sm:py-24" data-id="element-288" data-mp="3lgfn1xen6l94">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-289" data-mp="3litg60prhne1">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-290" data-mp="3gixbdz98prxf">
            <div data-id="element-291" data-mp="2xtlivt6kzns9">
              <p className="eyebrow" data-id="element-292" data-mp="2xu3o3fb81ccb">Contacto</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-293" data-mp="2xverhe0r8i2w">
                Cuéntanos qué necesita tu negocio ahora.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-294" data-mp="3pl9rc4m8nq5t">
              Completa el formulario y utilizaremos la información para entender
              el punto de partida de tu proyecto. Sin mensajes automatizados ni
              compromisos innecesarios.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell" data-id="element-295" data-mp="cdpv14a7aypy">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-296" data-mp="ccohmt5pq052">
          <div className="grid overflow-hidden border border-white/10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-297" data-mp="h5q01b54ohte">
            <aside className="flex flex-col bg-black p-8 sm:p-10 lg:border-r lg:border-white/10" data-id="element-298" data-mp="21tshso5pdzbo">
              <p className="eyebrow" data-id="element-299" data-mp="1wv8uajbgofh7">Después de escribirnos</p>
              <h2 className="mt-5 text-2xl font-medium tracking-[-0.035em] text-white" data-id="element-300" data-mp="2bkn4tuqrx2y6">
                Una primera conversación con contexto.
              </h2>
              <div className="mt-10 space-y-7" data-id="element-301" data-mp="26mljt49setk2">
                {contactSteps.map(step => {
                const StepIcon = step.icon;
                return <div key={step.title} className="flex gap-4" data-id="element-302" data-mp="1dxo3h6p4z6ty">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-white" data-id="element-303" data-mp="1dziqqrnvrb17">
                        <StepIcon size={16} aria-hidden="true" data-id="element-304" data-mp="1nvlv8467flrb" />
                      </div>
                      <div data-id="element-305" data-mp="349by57ll83zd">
                        <h3 className="text-sm font-medium text-white" data-id="element-306" data-mp="348iz6ssveirs">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-500" data-id="element-307" data-mp="2zcljmopflgg2">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-auto border-t border-white/10 pt-6" data-id="element-308" data-mp="1htk2tg41z8rg">
                <p className="flex items-center gap-2 text-xs font-medium text-neutral-300" data-id="element-309" data-mp="1mt55a0iflz8x">
                  <CheckCircle2Icon size={15} className="text-white" aria-hidden="true" data-id="element-310" data-mp="1mqsfj16fmklb" />{' '}
                  Tus datos se tratan de forma confidencial.
                </p>
              </div>
            </aside>

            <motion.form initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.08
          }} onSubmit={handleSubmit} className="bg-[#0a0a0a] p-8 sm:p-10" data-id="element-311" data-mp="21ib25htsrddn">
              <div className="grid gap-5 md:grid-cols-2" data-id="element-312" data-mp="2fhu0jiwt3acr">
                <label className="block" data-id="element-313" data-mp="2fic5r51g4ywt">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-314" data-mp="2plheppq6os5a">
                    Nombre completo *
                  </span>
                  <div className="relative" data-id="element-315" data-mp="2kp0ompzfc3pk">
                    <UserIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-316" data-mp="2fomhda5ncxsl" />
                    <input required value={formData.name} onChange={event => updateField('name', event.target.value)} className={`${inputClass} pl-11`} placeholder="Tu nombre" data-id="element-317" data-mp="2bnsr4a4qf6wj" />
                  </div>
                </label>
                <label className="block" data-id="element-318" data-mp="33fi6u0mmrgwo">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-319" data-mp="2yldwd7e5oqqu">
                    Negocio *
                  </span>
                  <input required value={formData.business} onChange={event => updateField('business', event.target.value)} className={inputClass} placeholder="Nombre de tu negocio" data-id="element-320" data-mp="3r2hygog43gmg" />
                </label>
                <label className="block" data-id="element-321" data-mp="1kdy2jfa2i1o2">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-322" data-mp="1kf9g26tbensd">
                    Correo electrónico *
                  </span>
                  <div className="relative" data-id="element-323" data-mp="1anwolgbfhzmv">
                    <MailIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-324" data-mp="1fnrf3irakn7e" />
                    <input required type="email" value={formData.email} onChange={event => updateField('email', event.target.value)} className={`${inputClass} pl-11`} placeholder="tu@email.com" data-id="element-325" data-mp="11nqc9xrgv5pa" />
                  </div>
                </label>
                <label className="block" data-id="element-326" data-mp="rrn4ab9xocuf">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-327" data-mp="wodfak6vsi8z">
                    Teléfono *
                  </span>
                  <div className="relative" data-id="element-328" data-mp="2m20d46z8yvkj">
                    <PhoneIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-329" data-mp="2r1v8amq227be" />
                    <input required type="tel" value={formData.phone} onChange={event => updateField('phone', event.target.value)} className={`${inputClass} pl-11`} placeholder="+34 600 000 000" data-id="element-330" data-mp="ffuhbl2qpj0k" />
                  </div>
                </label>
              </div>
              <label className="mt-5 block" data-id="element-331" data-mp="kdmht3hgadgd">
                <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-332" data-mp="kckw852t68ke">
                  Sector o actividad *
                </span>
                <select required value={formData.service} onChange={event => updateField('service', event.target.value)} className={inputClass} data-id="element-333" data-mp="u3xogxsir0qy">
                  <option value="" disabled data-id="element-334" data-mp="135ezi1r2kc8s">
                    Selecciona una opción
                  </option>
                  <option value="hosteleria" data-id="element-335" data-mp="39utjz1r981bk">Hostelería</option>
                  <option value="servicios" data-id="element-336" data-mp="39xg21vbrtz81">Servicios locales</option>
                  <option value="comercio" data-id="element-337" data-mp="34y35ujz0ffss">Comercio</option>
                  <option value="profesional" data-id="element-338" data-mp="2j3w6vv08kj6v">
                    Marca personal o profesional
                  </option>
                  <option value="otro" data-id="element-339" data-mp="2o14q7yx4et9p">Otro</option>
                </select>
              </label>
              <label className="mt-5 block" data-id="element-340" data-mp="2su7d5o7e74gx">
                <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-341" data-mp="2svifdjl6w45x">
                  Sobre el proyecto{' '}
                  <span className="normal-case tracking-normal text-neutral-600" data-id="element-342" data-mp="205s6pq2btuh7">
                    (opcional)
                  </span>
                </span>
                <textarea rows={5} value={formData.message} onChange={event => updateField('message', event.target.value)} className={`${inputClass} resize-none`} placeholder="Qué quieres mejorar, qué necesitas ordenar o qué objetivo tienes en mente." data-id="element-343" data-mp="2a2xy6zeurnhy" />
              </label>
              <button type="submit" disabled={formStatus !== 'idle'} className="mt-7 flex w-full items-center justify-center gap-2 bg-white px-5 py-4 text-sm font-semibold text-black transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-344" data-mp="3lk62lkpi13m4">
                {formStatus === 'idle' && <>
                    Enviar consulta{' '}
                    <ArrowRightIcon size={16} aria-hidden="true" data-id="element-345" data-mp="102keozbz258e" />
                  </>}
                {formStatus === 'submitting' && 'Enviando...'}
                {formStatus === 'success' && 'Consulta recibida'}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-neutral-600" data-id="element-346" data-mp="m4dqkawtrjlx">
                Al enviar, aceptas que utilicemos esta información para
                responder a tu consulta.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </div>;
}